import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import styles from './GuestProposalComparisonView.module.css';
import { proposalComparisonService } from '../../services/api';
import guestSessionService from '../../services/guestSessionService';
import MockAd from '../../components/ads/MockAd/MockAd';

const GuestProposalComparisonView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [comparisonData, setComparisonData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activePlanTab, setActivePlanTab] = useState('complete'); // Default to most popular plan

  // Pricing plans
  const pricingPlans = [
    {
      id: 'essential',
      name: 'Essential',
      price: 19.99,
      originalPrice: 39.99,
      popular: false,
      features: [
        'AI-powered analysis',
        'Compare up to 3 contractors',
        'Contractor behavior analysis questions',
        'Downloadable PDF report',
        'Summary of hidden costs',
        'Basic risk analysis for homeowner & project',
        'Basic cost breakdown',
        'Scoring for each contractor',
        'User preferences questions'
      ],
      keyFeatures: [
        {
          id: 'behavior_analysis',
          title: 'Contractor Behavior Analysis',
          description: 'Smart questions to evaluate contractor communication, reliability, and work style',
          icon: 'fas fa-user-check',
          previewData: {
            questions: [
              'How responsive is the contractor to your calls and emails?',
              'Does the contractor provide clear timelines and stick to them?',
              'How well does the contractor communicate project updates?'
            ],
            totalQuestions: 12
          }
        },
        {
          id: 'risk_analysis',
          title: 'Proposal Basic Risk Analysis',
          description: 'Identify potential red flags and hidden costs in contractor proposals',
          icon: 'fas fa-shield-alt',
          previewData: {
            riskCategories: ['Insurance Coverage', 'Payment Terms', 'Scope Clarity'],
            riskCount: 5,
            riskLevel: 'Medium'
          }
        },
        {
          id: 'contractor_scoring',
          title: 'Contractor Scoring System',
          description: 'Comprehensive scoring across multiple categories',
          icon: 'fas fa-star',
          previewData: {
            categories: ['Quality', 'Value', 'Communication', 'Timeline'],
            maxScore: 100,
            avgScore: 87
          }
        }
      ],
      icon: 'fa-file-alt',
      color: '#0077cc',
      bgGradient: 'linear-gradient(135deg, #0077cc 0%, #0053a6 100%)'
    },
    {
      id: 'complete',
      name: 'Complete',
      price: 39.99,
      originalPrice: 79.99,
      popular: true,
      features: [
        'Everything in Essential',
        'Compare up to 5 contractors',
        'Basic timeline evaluation',
        'Full comparison analysis',
        'Detailed cost breakdown',
        'Detailed allowance breakdown',
        'Missing elements detection',
        'Risk for homeowner & project',
        'Priority email support',
        'Custom contractor questions',
        'Detailed info on each contractor',
        'File storage (1GB)'
      ],
      keyFeatures: [
        {
          id: 'full_comparison',
          title: 'Full Comparison Analysis',
          description: 'Deep dive into every aspect of contractor proposals with detailed insights',
          icon: 'fas fa-chart-line',
          previewData: {
            analysisPoints: ['Cost Breakdown', 'Timeline Analysis', 'Quality Metrics', 'Risk Assessment'],
            insights: 47,
            categories: 12
          }
        },
        {
          id: 'missing_elements',
          title: 'Missing Elements Detection',
          description: 'AI identifies what contractors forgot to include in their proposals',
          icon: 'fas fa-search',
          previewData: {
            missingItems: [
              'Structural engineering assessment',
              'Permit application timeline',
              'Material delivery schedule',
              'Cleanup and disposal plan'
            ],
            totalGaps: 8,
            impact: 'High'
          }
        },
        {
          id: 'custom_questions',
          title: 'Custom Contractor Questions',
          description: 'Tailored questions based on your specific project and contractor weaknesses',
          icon: 'fas fa-question-circle',
          previewData: {
            questionCategories: ['Legal Protection', 'Timeline Management', 'Cost Control'],
            totalQuestions: 47,
            difficulty: 'Expert-level'
          }
        }
      ],
      icon: 'fa-crown',
      color: '#f59e0b',
      bgGradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
    },
    {
      id: 'expert',
      name: 'Expert',
      price: 79.99,
      originalPrice: 149.99,
      popular: false,
      features: [
        'Everything in Complete',
        'Negotiation strategy guide',
        'Priority phone support',
        'Project management tools',
        'Full file storage access',
        'AI-powered proposal generation',
        'Advanced risk modeling & timeline simulation',
        'File storage (5GB)'
      ],
      keyFeatures: [
        {
          id: 'negotiation_guide',
          title: 'Negotiation Strategy Guide',
          description: 'AI-powered negotiation tactics based on contractor weaknesses and market data',
          icon: 'fas fa-handshake',
          previewData: {
            strategies: [
              'Price reduction tactics based on competitor analysis',
              'Timeline negotiation leverage points',
              'Scope addition opportunities',
              'Payment term optimization'
            ],
            potentialSavings: '$12,000 - $25,000',
            successRate: '89%'
          }
        },
        {
          id: 'project_management',
          title: 'Project Management Tools',
          description: 'Complete project tracking with milestones, timelines, and progress monitoring',
          icon: 'fas fa-tasks',
          previewData: {
            features: ['Milestone Tracking', 'Progress Photos', 'Budget Monitoring', 'Quality Checklists'],
            phases: 8,
            checkpoints: 24
          }
        },
        {
          id: 'ai_proposal_generation',
          title: 'AI-Powered Proposal Generation',
          description: 'Generate counter-proposals and scope modifications using AI',
          icon: 'fas fa-robot',
          previewData: {
            capabilities: [
              'Generate alternative scope options',
              'Create detailed specifications',
              'Optimize cost vs. quality balance',
              'Timeline optimization suggestions'
            ],
            accuracy: '94%',
            timesSaved: '15+ hours'
          }
        }
      ],
      icon: 'fa-star',
      color: '#10b981',
      bgGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    }
  ];

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const fetchComparisonData = async () => {
      try {
        setIsLoading(true);
        const response = await proposalComparisonService.getComparisonById(id, true);
        
        if (response.data && response.data.status === 'success') {
          const data = response.data.data;
          setComparisonData(data);
          
          // Only save to guest session if this comparison doesn't already exist in sessions
          const existingSessions = guestSessionService.getGuestSessions();
          const existingSession = existingSessions.find(session => session.comparisonId === data.id);
          
          if (!existingSession) {
            // Save to guest session for later access only if it doesn't exist
            const metadata = {
              projectName: data.project_name,
              projectType: data.project_type,
              budget: data.budget,
              timeline: data.timeline,
              location: data.location,
              // Store the actual guest session ID that was used to create this comparison
              actualGuestSessionId: data.guest_session_id || proposalComparisonService.getGuestSessionId()
            };
            
            // Also add the guest session ID to the data object for easy access
            const sessionData = {
              ...data,
              guest_session_id: data.guest_session_id || proposalComparisonService.getGuestSessionId()
            };
            
            guestSessionService.saveGuestSession(sessionData, metadata);
            console.log('New guest session saved for comparison:', id);
          } else {
            // Update last accessed time for existing session
            existingSession.lastAccessed = new Date().toISOString();
            guestSessionService.updateGuestSession(existingSession.id, existingSession);
            console.log('Updated existing guest session for comparison:', id);
          }
        }
      } catch (err) {
        console.error("Error fetching comparison:", err);
        setError("Failed to load comparison data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchComparisonData();
  }, [id]);

  // Get contractor colors
  const getContractorColors = () => {
    if (!comparisonData || !comparisonData.comparison_data) return {};
    
    const colorPalette = ['#0077cc', '#f59e0b', '#10b981', '#ef4444'];
    let contractorNames = [];
    
    // Handle old format with scorecard
    if (comparisonData.comparison_data.comparison?.scorecard) {
      contractorNames = Object.keys(comparisonData.comparison_data.comparison.scorecard)
        .filter(key => key !== 'categories');
    } else {
      // Handle new format - use contractor names from the data structure
      if (comparisonData.comparison_data.Contractor_1_Information) {
        contractorNames.push('Contractor 1');
      }
      if (comparisonData.comparison_data.Contractor_2_Information) {
        contractorNames.push('Contractor 2');
      }
    }
    
    const colors = {};
    contractorNames.forEach((name, index) => {
      colors[name] = colorPalette[index % colorPalette.length];
    });
    
    return colors;
  };

  const contractorColors = getContractorColors();

  // Get the top 3 features to display dynamically
  const getTopFeatures = () => {
    if (!comparisonData?.comparison_data) return [];
    
    // If it's the old format with free_features, use them
    if (comparisonData.comparison_data.free_features) {
      const { features, top_3_features } = comparisonData.comparison_data.free_features;
      return top_3_features.map(featureId => 
        features.find(feature => feature.id === featureId)
      ).filter(Boolean);
    }
    
    // For new format, create features from actual risk alerts data
    const features = [];
    let totalRiskFlags = 0;
    let totalCostDifference = 0;
    let highRiskCount = 0;
    let mediumRiskCount = 0;
    
    // Count actual risk flags from both contractors
    const contractor1Risks = comparisonData.comparison_data.Contractor_1_Information?.Risk_Alerts || [];
    const contractor2Risks = comparisonData.comparison_data.Contractor_2_Information?.Risk_Alerts || [];
    
    totalRiskFlags = contractor1Risks.length + contractor2Risks.length;
    
    // Count by severity
    [...contractor1Risks, ...contractor2Risks].forEach(risk => {
      if (risk.flag === 'red') highRiskCount++;
      else if (risk.flag === 'yellow') mediumRiskCount++;
    });
    
    // Calculate actual cost difference
    const contractor1Cost = parseFloat(comparisonData.comparison_data.Contractor_1_Information?.Financials?.Base_Price?.amount?.replace(/[$,]/g, '') || 0);
    const contractor2Cost = parseFloat(comparisonData.comparison_data.Contractor_2_Information?.Financials?.Base_Price?.amount?.replace(/[$,]/g, '') || 0);
    totalCostDifference = Math.abs(contractor1Cost - contractor2Cost);
    
    // Only create features if we have actual data
    if (totalRiskFlags > 0) {
      features.push({
        id: 'hidden_risk_flags',
        title: 'Risk Analysis',
        priority: 95,
        icon: 'fas fa-exclamation-triangle',
        category: 'Risk Analysis',
        tag: 'Critical',
        color: '#ef4444',
        bgGradient: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        summary: `Found ${totalRiskFlags} risk areas identified in contractor proposals`,
        details: {
          risk_count: totalRiskFlags,
          high_risk: highRiskCount,
          medium_risk: mediumRiskCount,
          findings: [...contractor1Risks, ...contractor2Risks].slice(0, 3)
        }
      });
    }
    
    if (totalCostDifference > 0) {
      const percentDiff = contractor2Cost > 0 ? Math.round((totalCostDifference / Math.min(contractor1Cost, contractor2Cost)) * 100) : 0;
      features.push({
        id: 'pricing_analysis',
        title: 'Cost Analysis',
        priority: 82,
        icon: 'fas fa-dollar-sign',
        category: 'Cost Analysis',
        tag: 'Important',
        color: '#10b981',
        bgGradient: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        summary: `${percentDiff}% price difference between contractors`,
        details: {
          total_difference: totalCostDifference,
          percentage_difference: percentDiff,
          contractor1_cost: contractor1Cost,
          contractor2_cost: contractor2Cost
        }
      });
    }
    
    // Add comparison insights if available
    if (comparisonData.comparison_data.comparison || comparisonData.comparison_data.comparision) {
      const comparisonSection = comparisonData.comparison_data.comparison || comparisonData.comparison_data.comparision;
      const recommendation = comparisonSection.recommendation || comparisonSection.Recommendation;
      const scores = comparisonSection.total_scores || {
        'Contractor 1': comparisonSection.Contractor_1_Score?.Total,
        'Contractor 2': comparisonSection.Contractor_2_Score?.Total
      };
      
      features.push({
        id: 'comparison_insights',
        title: 'Contractor Comparison',
        priority: 88,
        icon: 'fas fa-chart-line',
        category: 'Analysis',
        tag: 'Insights',
        color: '#f59e0b',
        bgGradient: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        summary: recommendation?.Reason || recommendation?.reason || 'Comprehensive contractor analysis available',
        details: {
          recommendation: recommendation,
          scores: scores
        }
      });
    }
    
    return features.slice(0, 3);
  };

  // Prepare preview data (handle both old and new formats)  
  const prepareCostData = () => {
    if (!comparisonData?.comparison_data) return [];
    
    // Handle old format with proposals array
    if (comparisonData.comparison_data.proposals) {
      return comparisonData.comparison_data.proposals.map(proposal => ({
        name: proposal.name.length > 15 ? proposal.name.substring(0, 15) + '...' : proposal.name,
        cost: proposal.total_cost,
      }));
    }
    
    // Handle new format with contractor objects
    const costData = [];
    
    if (comparisonData.comparison_data.Contractor_1_Information) {
      const cost = parseFloat(comparisonData.comparison_data.Contractor_1_Information.Financials?.Base_Price?.amount?.replace(/[$,]/g, '') || 0);
      const name = 'Contractor 1';
      costData.push({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        cost: cost
      });
    }
    if (comparisonData.comparison_data.Contractor_2_Information) {
      const cost = parseFloat(comparisonData.comparison_data.Contractor_2_Information.Financials?.Base_Price?.amount?.replace(/[$,]/g, '') || 0);
      const name = 'Contractor 2';
      costData.push({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        cost: cost
      });
    }
    
    return costData;
  };

  const prepareScoreData = () => {
    if (!comparisonData?.comparison_data) return [];
    
    // Handle old format with comparison.total_scores
    if (comparisonData.comparison_data.comparison?.total_scores) {
      return Object.entries(comparisonData.comparison_data.comparison.total_scores)
        .map(([name, score]) => ({ 
          name: name.length > 15 ? name.substring(0, 15) + '...' : name, 
          score 
        }));
    }
    
    // Handle new format with comparision scores
    if (comparisonData.comparison_data.comparision) {
      const scoreData = [];
      if (comparisonData.comparison_data.comparision.Contractor_1_Score) {
        scoreData.push({
          name: 'Contractor 1',
          score: comparisonData.comparison_data.comparision.Contractor_1_Score.Total
        });
      }
      if (comparisonData.comparison_data.comparision.Contractor_2_Score) {
        scoreData.push({
          name: 'Contractor 2', 
          score: comparisonData.comparison_data.comparision.Contractor_2_Score.Total
        });
      }
      return scoreData;
    }
    
    return [];
  };

  const getRecommendation = () => {
    // Handle old format
    if (comparisonData?.comparison_data?.comparison?.recommendation?.best_choice) {
      return comparisonData.comparison_data.comparison.recommendation.best_choice;
    }
    
    // Handle new format with comparision
    if (comparisonData?.comparison_data?.comparision?.Recommendation?.Preferred_Contractor) {
      return comparisonData.comparison_data.comparision.Recommendation.Preferred_Contractor;
    }
    
    return 'Premium contractor recommended';
  };

  const getInsightPreview = () => {
    const costData = prepareCostData();
    if (costData.length < 2) return null;
    
    const highPrice = Math.max(...costData.map(d => d.cost));
    const lowPrice = Math.min(...costData.map(d => d.cost));
    const difference = highPrice - lowPrice;
    const percentDiff = Math.round((difference / lowPrice) * 100);
    
    return {
      priceDifference: difference,
      percentDiff,
      highPrice,
      lowPrice
    };
  };

  const handleUpgrade = (plan) => {
    setSelectedPlan(plan);
    setShowUpgradeModal(true);
  };

  const handleLoginRedirect = () => {
    navigate('/homeowner/login', { 
      state: { 
        returnTo: `/homeowner/comparisons/${id}`,
        message: 'Log in to access your full comparison report and save this project to your account' 
      }
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <div className={styles.loadingContent}>
            <h3>Analyzing Your Proposals...</h3>
            <p>Our AI is processing contractor data and generating insights</p>
            <div className={styles.loadingSteps}>
              <div className={styles.loadingStep}>✓ Reading proposals</div>
              <div className={styles.loadingStep}>✓ Analyzing costs</div>
              <div className={styles.loadingStep}>⚡ Generating insights</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !comparisonData) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <div className={styles.errorIcon}>
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h3>Oops! Something went wrong</h3>
          <p>{error || 'Comparison data not found'}</p>
          <button onClick={() => navigate('/')} className={styles.homeButton}>
            <i className="fas fa-home"></i> Go Home
          </button>
        </div>
      </div>
    );
  }

  const costData = prepareCostData();
  const scoreData = prepareScoreData();
  const insight = getInsightPreview();
  const topFeatures = getTopFeatures();
  const recommendation = getRecommendation();
  
  // Calculate total risk flags for display
  const totalRiskFlags = (() => {
    if (!comparisonData?.comparison_data) return 0;
    const contractor1Risks = comparisonData.comparison_data.Contractor_1_Information?.Risk_Alerts?.length || 0;
    const contractor2Risks = comparisonData.comparison_data.Contractor_2_Information?.Risk_Alerts?.length || 0;
    return contractor1Risks + contractor2Risks;
  })();

  return (
    <div className={styles.guestContainer}>
      {/* Navigation */}
      {/* <nav className={styles.navigation}>
        <div className={styles.navContent}>
          <div className={styles.logo}>
            <i className="fas fa-chart-line"></i>
            <span>SmartTech ProServe</span>
          </div>
          <div className={styles.navActions}>
            <button className={styles.loginBtn} onClick={handleLoginRedirect}>
              <i className="fas fa-sign-in-alt"></i>
              Log In
            </button>
          </div>
        </div>
      </nav> */}

      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <div className={styles.heroGrid}></div>
          <div className={styles.heroGradient}></div>
        </div>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <div className={styles.heroTag}>
              <i className="fas fa-check-circle"></i>
              Analysis Complete
            </div>
            <h1>Your Contractor Analysis is Ready!</h1>
            <p className={styles.heroSubtitle}>
              Our AI has analyzed your proposals and discovered <strong>game-changing insights</strong> that could save you thousands of dollars
            </p>
            
            <div className={styles.heroStats}>
              {topFeatures.map((feature, index) => (
                <div key={feature.id} className={styles.heroStat}>
                  <div className={styles.statContent}>
                    <div className={styles.statTop}>
                      <div className={styles.statIcon}>
                        <i className={feature.icon}></i>
                      </div>
                      <div className={styles.statNumber}>
                        {feature.id === 'hidden_risk_flags' && feature.details.risk_count}
                        {feature.id === 'pricing_analysis' && `$${(feature.details.total_difference/1000).toFixed(0)}K`}
                        {feature.id === 'comparison_insights' && Object.keys(feature.details.scores || {}).length}
                      </div>
                    </div>
                    <div className={styles.statLabel}>
                      {feature.id === 'hidden_risk_flags' && 'Risk Flags Found'}
                      {feature.id === 'pricing_analysis' && 'Price Difference'}
                      {feature.id === 'comparison_insights' && 'Analysis Complete'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className={styles.heroCTA}>
              <button className={styles.primaryCTA} onClick={() => setShowUpgradeModal(true)}>
                <i className="fas fa-unlock"></i>
                View Complete Analysis
              </button>
            </div>
          </div>
          
          <div className={styles.heroVisual}>
            <div className={styles.reportPreview}>
              <div className={styles.previewHeader}>
                <div className={styles.previewButtons}>
                  <div className={styles.previewButton}></div>
                  <div className={styles.previewButton}></div>
                  <div className={styles.previewButton}></div>
                </div>
                <div className={styles.previewTitle}>Analysis Report</div>
              </div>
              <div className={styles.previewContent}>
                <div className={styles.previewChart}>
                  <ResponsiveContainer 
                    width="100%" 
                    height={120}
                    key={`hero-chart-${(scoreData.length > 0 ? scoreData : costData).length}`}
                  >
                    <BarChart data={scoreData.length > 0 ? scoreData : costData}>
                      <Bar 
                        dataKey={scoreData.length > 0 ? "score" : "cost"} 
                        fill="url(#chartGradient)" 
                        radius={[4, 4, 0, 0]} 
                      />
                      <defs>
                        <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0077cc" />
                          <stop offset="100%" stopColor="#0053a6" />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className={styles.previewMetrics}>
                  <div className={styles.previewMetric}>
                    <div className={styles.metricDot}></div>
                    <span>
                      {scoreData.length > 0 
                        ? `Average Score: ${Math.round(scoreData.reduce((acc, item) => acc + item.score, 0) / scoreData.length)}/100`
                        : `Average Cost: $${costData.length > 0 ? Math.round(costData.reduce((acc, item) => acc + item.cost, 0) / costData.length).toLocaleString() : '0'}`
                      }
                    </span>
                  </div>
                  <div className={styles.previewMetric}>
                    <div className={styles.metricDot}></div>
                    <span>Risk Level: Low</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mock Ad for Testing - Remove when using real ads */}
      <MockAd placement="banner"/>

      {/* Quick Insights Preview */}
      <section className={styles.insightsSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionBadge}>
            <i className="fas fa-sparkles"></i>
            <span>AI-Powered Analysis</span>
          </div>
          <h2>Quick Insights Preview</h2>
          <p>Here's a taste of what our AI discovered about your contractors</p>
        </div>

        <div className={styles.insightCards}>
          {/* Recommendation Card */}
          <div className={styles.insightCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIconWrapper}>
                <div className={styles.cardIcon}>
                  <i className="fas fa-award"></i>
                </div>
                <div className={styles.cardHeaderText}>
                  <h3>Top Recommendation</h3>
                  <div className={styles.cardTag}>
                    <i className="fas fa-robot"></i>
                    AI Powered
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.recommendationBadge}>
                <i className="fas fa-star"></i>
                <span>{recommendation}</span>
              </div>
              <p>Based on comprehensive analysis of pricing, quality, and risk factors</p>
              <div className={styles.recommendationStats}>
                <div className={styles.recStat}>
                  <div className={styles.recStatIcon}>
                    <i className="fas fa-bullseye"></i>
                  </div>
                  <div className={styles.recStatContent}>
                    <span className={styles.recStatValue}>94%</span>
                    <span className={styles.recStatLabel}>Match Score</span>
                  </div>
                </div>
                <div className={styles.recStat}>
                  <div className={styles.recStatIcon}>
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div className={styles.recStatContent}>
                    <span className={styles.recStatValue}>Low</span>
                    <span className={styles.recStatLabel}>Risk Level</span>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.cardBlur}>
              <div className={styles.blurOverlay}>
                <div className={styles.unlockIcon}>
                  <i className="fas fa-lock"></i>
                </div>
                <div className={styles.unlockText}>
                  <span className={styles.unlockTitle}>See detailed reasoning</span>
                  <span className={styles.unlockSubtitle}>Unlock full analysis</span>
                </div>
              </div>
            </div>
          </div>

          {/* Cost Analysis Card */}
          <div className={styles.insightCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIconWrapper}>
                <div className={styles.cardIcon}>
                  <i className="fas fa-chart-bar"></i>
                </div>
                <div className={styles.cardHeaderText}>
                  <h3>Cost Analysis</h3>
                  <div className={styles.cardTag}>
                    <i className="fas fa-database"></i>
                    Live Data
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={costData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} tick={{ fontSize: 12, fill: '#64748b' }} />
                    <Tooltip 
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']}
                      contentStyle={{ 
                        backgroundColor: '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar dataKey="cost" fill="url(#costGradient)" radius={[6, 6, 0, 0]} />
                    <defs>
                      <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0077cc" />
                        <stop offset="100%" stopColor="#0053a6" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {insight && (
                <div className={styles.costInsight}>
                  <div className={styles.insightHighlight}>
                    <div className={styles.highlightIcon}>
                      <i className="fas fa-percentage"></i>
                    </div>
                    <div className={styles.highlightContent}>
                      <span className={styles.highlightValue}>{insight.percentDiff}%</span>
                      <span className={styles.highlightLabel}>price difference</span>
                    </div>
                  </div>
                  <p>Range: <strong>${Math.min(...costData.map(d => d.cost)).toLocaleString()}</strong> to <strong>${Math.max(...costData.map(d => d.cost)).toLocaleString()}</strong></p>
                </div>
              )}
            </div>
            <div className={styles.cardBlur}>
              <div className={styles.blurOverlay}>
                <div className={styles.unlockIcon}>
                  <i className="fas fa-lock"></i>
                </div>
                <div className={styles.unlockText}>
                  <span className={styles.unlockTitle}>Unlock detailed breakdown</span>
                  <span className={styles.unlockSubtitle}>See cost comparisons</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quality Scores Card */}
          <div className={styles.insightCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIconWrapper}>
                <div className={styles.cardIcon}>
                  <i className="fas fa-star"></i>
                </div>
                <div className={styles.cardHeaderText}>
                  <h3>Quality Scores</h3>
                  <div className={styles.cardTag}>
                    <i className="fas fa-check-circle"></i>
                    Verified
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.scorePreview}>
                {scoreData.slice(0, 2).map((item, index) => {
                  // Ensure score is properly normalized to 0-100 scale
                  const normalizedScore = Math.min(Math.max(item.score, 0), 100);
                  return (
                    <div key={index} className={styles.scoreItem}>
                      <div className={styles.scoreHeader}>
                        <div className={styles.contractorAvatar}>
                          <span>{item.name.charAt(0)}</span>
                        </div>
                        <div className={styles.scoreDetails}>
                          <div className={styles.scoreName}>{item.name}</div>
                          <div className={styles.scoreValue}>{Math.round(normalizedScore)}/100</div>
                        </div>
                      </div>
                      <div className={styles.scoreBar}>
                        <div 
                          className={styles.scoreBarFill}
                          style={{ 
                            width: `${normalizedScore}%`,
                            background: Object.values(contractorColors)[index] || '#0077cc'
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={styles.scoresSummary}>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryIcon}>
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <span>Quality verified across 12 categories</span>
                </div>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryIcon}>
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <span>Performance metrics analyzed</span>
                </div>
              </div>
            </div>
            <div className={styles.cardBlur}>
              <div className={styles.blurOverlay}>
                <div className={styles.unlockIcon}>
                  <i className="fas fa-lock"></i>
                </div>
                <div className={styles.unlockText}>
                  <span className={styles.unlockTitle}>See all categories</span>
                  <span className={styles.unlockSubtitle}>Complete quality report</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mock Ad for Testing - Remove when using real ads */}
      <MockAd placement="inline"/>

      {/* Premium Content Teasers */}
      <section className={styles.premiumSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionBadge}>
            <i className="fas fa-crown"></i>
            <span>Premium Features</span>
          </div>
          <h2>What You'll Get in the Full Report</h2>
          <p>Unlock comprehensive insights that could save you thousands</p>
        </div>
        
        <div className={styles.premiumGrid}>
          <div className={styles.premiumCard}>
            <div className={styles.premiumCardHeader}>
              <div className={styles.premiumIcon}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div className={styles.premiumHeaderText}>
                <h3>Risk Analysis</h3>
                <div className={styles.premiumBadge}>
                  <i className="fas fa-shield-alt"></i>
                  <span>Critical</span>
                </div>
              </div>
            </div>
            <div className={styles.premiumPreview}>
              {/* Show actual risk alerts from the data */}
              {comparisonData?.comparison_data?.Contractor_1_Information?.Risk_Alerts?.slice(0, 2).map((risk, index) => (
                <div key={index} className={styles.riskItem}>
                  <div className={styles.riskIndicator}>
                    <i className={`fas ${risk.flag === 'red' ? 'fa-times-circle' : 'fa-exclamation-triangle'}`}></i>
                  </div>
                  <div className={styles.riskContent}>
                    <span className={styles.riskLevel}>
                      {risk.flag === 'red' ? '🚨 High Risk' : '⚠️ Medium Risk'}
                    </span>
                    <span className={styles.riskDescription}>{risk.title}</span>
                  </div>
                </div>
              )) || (
                // Fallback if no risk data
                <>
                  <div className={styles.riskItem}>
                    <div className={styles.riskIndicator}>
                      <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <div className={styles.riskContent}>
                      <span className={styles.riskLevel}>⚠️ Medium Risk</span>
                      <span className={styles.riskDescription}>Missing warranty details</span>
                    </div>
                  </div>
                  <div className={styles.riskItem}>
                    <div className={styles.riskIndicator}>
                      <i className="fas fa-times-circle"></i>
                    </div>
                    <div className={styles.riskContent}>
                      <span className={styles.riskLevel}>🚨 High Risk</span>
                      <span className={styles.riskDescription}>Incomplete scope coverage</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={styles.premiumBlur}>
              <div className={styles.blurContent}>
                <div className={styles.premiumUnlockIcon}>
                  <i className="fas fa-shield-alt"></i>
                </div>
                <div className={styles.premiumUnlockText}>
                  <p className={styles.premiumUnlockTitle}><strong>Complete risk assessment</strong></p>
                  <p className={styles.premiumUnlockSubtitle}>+ {Math.max(0, totalRiskFlags - 2)} more critical factors</p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.premiumCard}>
            <div className={styles.premiumCardHeader}>
              <div className={styles.premiumIcon}>
                <i className="fas fa-question-circle"></i>
              </div>
              <div className={styles.premiumHeaderText}>
                <h3>Expert Questions</h3>
                <div className={styles.premiumBadge}>
                  <i className="fas fa-user-tie"></i>
                  <span>Expert</span>
                </div>
              </div>
            </div>
            <div className={styles.premiumPreview}>
              {/* Show actual negotiation questions from the data */}
              {comparisonData?.comparison_data?.comparision?.Negotiation_Questions?.Contractor_1?.slice(0, 2).map((question, index) => (
                <div key={index} className={styles.questionItem}>
                  <div className={styles.questionIcon}>
                    <i className="fas fa-quote-left"></i>
                  </div>
                  <div className={styles.questionText}>
                    "{question}"
                  </div>
                </div>
              )) || (
                // Fallback if no question data
                <>
                  <div className={styles.questionItem}>
                    <div className={styles.questionIcon}>
                      <i className="fas fa-quote-left"></i>
                    </div>
                    <div className={styles.questionText}>
                      "What's included in your warranty coverage?"
                    </div>
                  </div>
                  <div className={styles.questionItem}>
                    <div className={styles.questionIcon}>
                      <i className="fas fa-quote-left"></i>
                    </div>
                    <div className={styles.questionText}>
                      "How do you handle change orders?"
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={styles.premiumBlur}>
              <div className={styles.blurContent}>
                <div className={styles.premiumUnlockIcon}>
                  <i className="fas fa-list"></i>
                </div>
                <div className={styles.premiumUnlockText}>
                  <p className={styles.premiumUnlockTitle}><strong>{
                    (comparisonData?.comparison_data?.comparision?.Negotiation_Questions?.Contractor_1?.length || 0) +
                    (comparisonData?.comparison_data?.comparision?.Negotiation_Questions?.Contractor_2?.length || 0) || 10
                  } expert questions</strong></p>
                  <p className={styles.premiumUnlockSubtitle}>Tailored to your contractors</p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.premiumCard}>
            <div className={styles.premiumCardHeader}>
              <div className={styles.premiumIcon}>
                <i className="fas fa-calendar-alt"></i>
              </div>
              <div className={styles.premiumHeaderText}>
                <h3>Timeline Analysis</h3>
                <div className={styles.premiumBadge}>
                  <i className="fas fa-clock"></i>
                  <span>Detailed</span>
                </div>
              </div>
            </div>
            <div className={styles.premiumPreview}>
              <div className={styles.timelineItem}>
                <div className={styles.timelinePhase}>
                  <div className={styles.phaseIcon}>
                    <i className="fas fa-drafting-compass"></i>
                  </div>
                  <div className={styles.phaseContent}>
                    <span className={styles.phaseName}>Project Duration</span>
                    <span className={styles.phaseDuration}>
                      {comparisonData?.comparison_data?.Contractor_1_Information?.Timeline?.Project_Duration || '12 weeks'}
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.timelineItem}>
                <div className={styles.timelinePhase}>
                  <div className={styles.phaseIcon}>
                    <i className="fas fa-hard-hat"></i>
                  </div>
                  <div className={styles.phaseContent}>
                    <span className={styles.phaseName}>Contractor 2 Timeline</span>
                    <span className={styles.phaseDuration}>
                      {comparisonData?.comparison_data?.Contractor_2_Information?.Timeline?.Project_Duration || 'Not specified'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.premiumBlur}>
              <div className={styles.blurContent}>
                <div className={styles.premiumUnlockIcon}>
                  <i className="fas fa-clock"></i>
                </div>
                <div className={styles.premiumUnlockText}>
                  <p className={styles.premiumUnlockTitle}><strong>Detailed milestone tracking</strong></p>
                  <p className={styles.premiumUnlockSubtitle}>Critical path analysis</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mock Ad for Testing - Remove when using real ads */}
      <MockAd placement="inline"/>

      {/* Essential Plan Features Section */}
      <section className={styles.planFeaturesSection}>
        <div className={styles.planFeaturesHeader}>
          <div className={styles.planFeatureBadge} style={{ background: pricingPlans[0].bgGradient }}>
            <i className={`fas ${pricingPlans[0].icon}`}></i>
            <span>{pricingPlans[0].name} Features</span>
          </div>
          <h2>Essential Plan Deep Dive</h2>
          <p>Perfect for homeowners who want solid analysis with AI assistance</p>
        </div>
        
        <div className={styles.planFeaturesGrid}>
          {pricingPlans[0].keyFeatures.map((feature, index) => (
            <div key={feature.id} className={styles.planFeatureCard} onClick={() => handleUpgrade(pricingPlans[0])}>
              <div className={styles.planFeatureHeader}>
                <div className={styles.planFeatureIcon} style={{ background: pricingPlans[0].bgGradient }}>
                  <i className={feature.icon}></i>
                </div>
                <div className={styles.planFeatureHeaderText}>
                  <h3>{feature.title}</h3>
                  <div className={styles.planFeatureTag}>
                    <i className="fas fa-play"></i>
                    <span>Starter</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.planFeatureContent}>
                <p className={styles.planFeatureDescription}>{feature.description}</p>
                
                {feature.id === 'behavior_analysis' && (
                  <div className={styles.featurePreview}>
                    <div className={styles.previewTitle}>Sample Questions:</div>
                    <div className={styles.questionsList}>
                      {feature.previewData.questions.slice(0, 2).map((question, idx) => (
                        <div key={idx} className={styles.questionItem}>
                          <div className={styles.questionIcon}><i className="fas fa-question"></i></div>
                          <span>{question}</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.previewStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statValue}>{feature.previewData.totalQuestions}</span>
                        <span className={styles.statLabel}>Total Questions</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {feature.id === 'risk_analysis' && (
                  <div className={styles.featurePreview}>
                    <div className={styles.riskMetrics}>
                      <div className={styles.riskItem}>
                        <div className={styles.riskLevel}>
                          <span className={styles.riskIcon}>⚠️</span>
                          <span>{feature.previewData.riskLevel} Risk</span>
                        </div>
                        <span className={styles.riskCount}>{feature.previewData.riskCount} flags detected</span>
                      </div>
                    </div>
                    <div className={styles.riskCategories}>
                      {feature.previewData.riskCategories.map((category, idx) => (
                        <div key={idx} className={styles.riskCategory}>
                          <i className="fas fa-shield-alt"></i>
                          <span>{category}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {feature.id === 'contractor_scoring' && (
                  <div className={styles.featurePreview}>
                    <div className={styles.scoreDisplay}>
                      <div className={styles.scoreMeter}>
                        <div className={styles.scoreValue}>{feature.previewData.avgScore}/100</div>
                        <div className={styles.scoreLabel}>Average Score</div>
                      </div>
                    </div>
                    <div className={styles.scoreCategories}>
                      {feature.previewData.categories.map((category, idx) => (
                        <div key={idx} className={styles.scoreCategory}>
                          <span className={styles.categoryName}>{category}</span>
                          <div className={styles.categoryBar}>
                            <div 
                              className={styles.categoryFill} 
                              style={{ width: `${85 + idx * 3}%`, background: pricingPlans[0].bgGradient }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className={styles.planFeatureBlur}>
                <div className={styles.featureBlurOverlay}>
                  <div className={styles.featureUnlockIcon}>
                    <i className="fas fa-lock"></i>
                  </div>
                  <div className={styles.featureUnlockText}>
                    <span className={styles.featureUnlockTitle}>
                      {feature.id === 'behavior_analysis' && 'Complete behavioral assessment'}
                      {feature.id === 'risk_analysis' && 'Complete risk assessment'}
                      {feature.id === 'contractor_scoring' && 'Full scoring breakdown'}
                    </span>
                    <span className={styles.featureUnlockSubtitle}>
                      {feature.id === 'behavior_analysis' && '+ 10 more behavior questions'}
                      {feature.id === 'risk_analysis' && '+ 8 more critical factors'}
                      {feature.id === 'contractor_scoring' && '+ 9 more scoring categories'}
                    </span>
                    <span className={styles.featureUnlockPrice}>${pricingPlans[0].price}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Complete Plan Features Section */}
      <section className={styles.planFeaturesSection}>
        <div className={styles.planFeaturesHeader}>
          <div className={styles.planFeatureBadge} style={{ background: pricingPlans[1].bgGradient }}>
            <i className={`fas ${pricingPlans[1].icon}`}></i>
            <span>{pricingPlans[1].name} Features</span>
          </div>
          <h2>Complete Plan Deep Dive</h2>
          <p>For serious planners who want detailed insights and comprehensive comparisons</p>
        </div>
        
        <div className={styles.planFeaturesGrid}>
          {pricingPlans[1].keyFeatures.map((feature, index) => (
            <div key={feature.id} className={styles.planFeatureCard} onClick={() => handleUpgrade(pricingPlans[1])}>
              <div className={styles.planFeatureHeader}>
                <div className={styles.planFeatureIcon} style={{ background: pricingPlans[1].bgGradient }}>
                  <i className={feature.icon}></i>
                </div>
                <div className={styles.planFeatureHeaderText}>
                  <h3>{feature.title}</h3>
                  <div className={styles.planFeatureTag}>
                    <i className="fas fa-fire"></i>
                    <span>Most Popular</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.planFeatureContent}>
                <p className={styles.planFeatureDescription}>{feature.description}</p>
                
                {feature.id === 'full_comparison' && (
                  <div className={styles.featurePreview}>
                    <div className={styles.analysisGrid}>
                      {feature.previewData.analysisPoints.map((point, idx) => (
                        <div key={idx} className={styles.analysisPoint}>
                          <div className={styles.analysisIcon}>
                            <i className="fas fa-chart-bar"></i>
                          </div>
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.previewStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statValue}>{feature.previewData.insights}</span>
                        <span className={styles.statLabel}>AI Insights</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statValue}>{feature.previewData.categories}</span>
                        <span className={styles.statLabel}>Categories</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {feature.id === 'missing_elements' && (
                  <div className={styles.featurePreview}>
                    <div className={styles.missingItemsList}>
                      {feature.previewData.missingItems.slice(0, 2).map((item, idx) => (
                        <div key={idx} className={styles.missingItem}>
                          <div className={styles.missingIcon}>
                            <i className="fas fa-exclamation-triangle"></i>
                          </div>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.gapsSummary}>
                      <div className={styles.gapsCount}>
                        <span className={styles.gapsNumber}>{feature.previewData.totalGaps}</span>
                        <span className={styles.gapsLabel}>Gaps Found</span>
                      </div>
                      <div className={styles.impactLevel}>
                        <span className={styles.impactIcon}>🚨</span>
                        <span>{feature.previewData.impact} Impact</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {feature.id === 'custom_questions' && (
                  <div className={styles.featurePreview}>
                    <div className={styles.questionCategories}>
                      {feature.previewData.questionCategories.map((category, idx) => (
                        <div key={idx} className={styles.questionCategory}>
                          <div className={styles.categoryIcon}>
                            <i className="fas fa-question-circle"></i>
                          </div>
                          <span>{category}</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.questionsStats}>
                      <div className={styles.questionsStat}>
                        <span className={styles.questionsNumber}>{feature.previewData.totalQuestions}</span>
                        <span className={styles.questionsLabel}>Expert Questions</span>
                      </div>
                      <div className={styles.difficultyBadge}>
                        <i className="fas fa-graduation-cap"></i>
                        <span>{feature.previewData.difficulty}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className={styles.planFeatureBlur}>
                <div className={styles.featureBlurOverlay}>
                  <div className={styles.featureUnlockIcon}>
                    <i className="fas fa-lock"></i>
                  </div>
                  <div className={styles.featureUnlockText}>
                    <span className={styles.featureUnlockTitle}>
                      {feature.id === 'full_comparison' && 'Complete comparison analysis'}
                      {feature.id === 'missing_elements' && 'All missing elements detected'}
                      {feature.id === 'custom_questions' && 'Full expert question set'}
                    </span>
                    <span className={styles.featureUnlockSubtitle}>
                      {feature.id === 'full_comparison' && '+ 35 more AI insights'}
                      {feature.id === 'missing_elements' && '+ 6 more missing items'}
                      {feature.id === 'custom_questions' && '+ 44 more expert questions'}
                    </span>
                    <span className={styles.featureUnlockPrice}>${pricingPlans[1].price}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Expert Plan Features Section */}
      <section className={styles.planFeaturesSection}>
        <div className={styles.planFeaturesHeader}>
          <div className={styles.planFeatureBadge} style={{ background: pricingPlans[2].bgGradient }}>
            <i className={`fas ${pricingPlans[2].icon}`}></i>
            <span>{pricingPlans[2].name} Features</span>
          </div>
          <h2>Expert Plan Deep Dive</h2>
          <p>For professionals and homeowners handling large or complex projects</p>
        </div>
        
        <div className={styles.planFeaturesGrid}>
          {pricingPlans[2].keyFeatures.map((feature, index) => (
            <div key={feature.id} className={styles.planFeatureCard} onClick={() => handleUpgrade(pricingPlans[2])}>
              <div className={styles.planFeatureHeader}>
                <div className={styles.planFeatureIcon} style={{ background: pricingPlans[2].bgGradient }}>
                  <i className={feature.icon}></i>
                </div>
                <div className={styles.planFeatureHeaderText}>
                  <h3>{feature.title}</h3>
                  <div className={styles.planFeatureTag}>
                    <i className="fas fa-gem"></i>
                    <span>Premium</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.planFeatureContent}>
                <p className={styles.planFeatureDescription}>{feature.description}</p>
                
                {feature.id === 'negotiation_guide' && (
                  <div className={styles.featurePreview}>
                    <div className={styles.strategiesList}>
                      {feature.previewData.strategies.slice(0, 2).map((strategy, idx) => (
                        <div key={idx} className={styles.strategyItem}>
                          <div className={styles.strategyIcon}>
                            <i className="fas fa-lightbulb"></i>
                          </div>
                          <span>{strategy}</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.savingsPreview}>
                      <div className={styles.savingsAmount}>
                        <span className={styles.savingsValue}>{feature.previewData.potentialSavings}</span>
                        <span className={styles.savingsLabel}>Potential Savings</span>
                      </div>
                      <div className={styles.successRate}>
                        <span className={styles.successIcon}>✅</span>
                        <span>{feature.previewData.successRate} Success Rate</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {feature.id === 'project_management' && (
                  <div className={styles.featurePreview}>
                    <div className={styles.managementFeatures}>
                      {feature.previewData.features.map((mgmtFeature, idx) => (
                        <div key={idx} className={styles.managementFeature}>
                          <div className={styles.mgmtIcon}>
                            <i className="fas fa-check-circle"></i>
                          </div>
                          <span>{mgmtFeature}</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.projectStats}>
                      <div className={styles.projectStat}>
                        <span className={styles.projectNumber}>{feature.previewData.phases}</span>
                        <span className={styles.projectLabel}>Project Phases</span>
                      </div>
                      <div className={styles.projectStat}>
                        <span className={styles.projectNumber}>{feature.previewData.checkpoints}</span>
                        <span className={styles.projectLabel}>Checkpoints</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {feature.id === 'ai_proposal_generation' && (
                  <div className={styles.featurePreview}>
                    <div className={styles.aiCapabilities}>
                      {feature.previewData.capabilities.slice(0, 2).map((capability, idx) => (
                        <div key={idx} className={styles.aiCapability}>
                          <div className={styles.aiIcon}>
                            <i className="fas fa-robot"></i>
                          </div>
                          <span>{capability}</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.aiStats}>
                      <div className={styles.aiStat}>
                        <span className={styles.aiValue}>{feature.previewData.accuracy}</span>
                        <span className={styles.aiLabel}>AI Accuracy</span>
                      </div>
                      <div className={styles.aiStat}>
                        <span className={styles.aiValue}>{feature.previewData.timesSaved}</span>
                        <span className={styles.aiLabel}>Time Saved</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className={styles.planFeatureBlur}>
                <div className={styles.featureBlurOverlay}>
                  <div className={styles.featureUnlockIcon}>
                    <i className="fas fa-lock"></i>
                  </div>
                  <div className={styles.featureUnlockText}>
                    <span className={styles.featureUnlockTitle}>
                      {feature.id === 'negotiation_guide' && 'Complete negotiation strategy'}
                      {feature.id === 'project_management' && 'Full project management suite'}
                      {feature.id === 'ai_proposal_generation' && 'Advanced AI proposal tools'}
                    </span>
                    <span className={styles.featureUnlockSubtitle}>
                      {feature.id === 'negotiation_guide' && '+ 2 more negotiation tactics'}
                      {feature.id === 'project_management' && '+ 16 more checkpoints'}
                      {feature.id === 'ai_proposal_generation' && '+ 2 more AI capabilities'}
                    </span>
                    <span className={styles.featureUnlockPrice}>${pricingPlans[2].price}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Mock Ad for Testing - Remove when using real ads */}
      <MockAd placement="banner"/>
      {/* Pricing Section */}
      <section className={styles.pricingSection}>
        <div className={styles.sectionHeader}>
          <h2>Choose Your Analysis Package</h2>
          <p>Limited time offer - Save up to 50% on detailed contractor analysis</p>
        </div>

        {/* Mobile Tab Selector */}
        <div className={styles.mobileTabSelector}>
          <div className={styles.tabButtons}>
            {pricingPlans.map((plan) => (
              <button
                key={plan.id}
                className={`${styles.tabButton} ${activePlanTab === plan.id ? styles.activeTab : ''}`}
                onClick={() => setActivePlanTab(plan.id)}
              >
                <div className={styles.tabIcon}>
                  <i className={`fas ${plan.icon}`}></i>
                </div>
                <div className={styles.tabInfo}>
                  <span className={styles.tabName}>{plan.name}</span>
                  <span className={styles.tabPrice}>${plan.price}</span>
                </div>
                {plan.popular && (
                  <div className={styles.tabBadge}>
                    <i className="fas fa-fire"></i>
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className={styles.tabContent}>
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`${styles.tabPane} ${activePlanTab === plan.id ? styles.activePane : ''}`}
              >
                <div className={styles.mobileplanHeader}>
                  <div className={styles.planPricing}>
                    <span className={styles.originalPrice}>${plan.originalPrice}</span>
                    <span className={styles.currentPrice}>${plan.price}</span>
                  </div>
                  <div className={styles.savings}>
                    Save ${(plan.originalPrice - plan.price).toFixed(2)}
                  </div>
                  {plan.popular && (
                    <div className={styles.mobilePopularBadge}>
                      <i className="fas fa-fire"></i>
                      Most Popular
                    </div>
                  )}
                </div>

                <div className={styles.mobilePlanFeatures}>
                  {plan.features.map((feature, index) => (
                    <div key={index} className={styles.mobileFeature}>
                      <i className="fas fa-check"></i>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <button 
                  className={`${styles.mobilePlanButton} ${plan.popular ? styles.mobilePopularButton : ''}`}
                  onClick={() => handleUpgrade(plan)}
                  style={{ background: plan.bgGradient }}
                >
                  Get {plan.name}
                  <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop Grid - Hidden on Mobile */}
        <div className={styles.pricingGrid}>
          {pricingPlans.map((plan) => (
            <div 
              key={plan.id} 
              className={`${styles.pricingCard} ${plan.popular ? styles.popularPlan : ''}`}
            >
              {plan.popular && (
                <div className={styles.popularBadge}>
                  <i className="fas fa-fire"></i>
                  Most Popular
                </div>
              )}
              
              <div className={styles.planHeader}>
                <div className={styles.planIcon} style={{ background: plan.bgGradient }}>
                  <i className={`fas ${plan.icon}`}></i>
                </div>
                <h3>{plan.name}</h3>
                <div className={styles.planPricing}>
                  <span className={styles.originalPrice}>${plan.originalPrice}</span>
                  <span className={styles.currentPrice}>${plan.price}</span>
                </div>
                <div className={styles.savings}>
                  Save ${(plan.originalPrice - plan.price).toFixed(2)}
                </div>
              </div>

              <div className={styles.planFeatures}>
                {plan.features.map((feature, index) => (
                  <div key={index} className={styles.feature}>
                    <i className="fas fa-check"></i>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <button 
                className={`${styles.planButton} ${plan.popular ? styles.popularButton : ''}`}
                onClick={() => handleUpgrade(plan)}
                style={{ background: plan.bgGradient }}
              >
                Get {plan.name}
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Mock Ad for Testing - Remove when using real ads */}
      <MockAd placement="inline"/>

      {/* Social Proof */}
      <section className={styles.socialProofSection}>
        <div className={styles.proofContent}>
          <h3>Trusted Analysis Platform</h3>
          <div className={styles.proofStats}>
            <div className={styles.proofStat}>
              <div className={styles.proofNumber}>{totalRiskFlags || 10}+</div>
              <div className={styles.proofLabel}>Risk Flags Detected</div>
            </div>
            <div className={styles.proofStat}>
              <div className={styles.proofNumber}>{costData.length}</div>
              <div className={styles.proofLabel}>Contractors Analyzed</div>
            </div>
            <div className={styles.proofStat}>
              <div className={styles.proofNumber}>${Math.round(insight?.priceDifference/1000) || 150}K</div>
              <div className={styles.proofLabel}>Price Difference Found</div>
            </div>
          </div>
          <div className={styles.trustIndicators}>
            <div className={styles.trustItem}>
              <i className="fas fa-lock"></i>
              <span>Secure & confidential</span>
            </div>
            <div className={styles.trustItem}>
              <i className="fas fa-clock"></i>
              <span>Instant analysis</span>
            </div>
            <div className={styles.trustItem}>
              <i className="fas fa-chart-line"></i>
              <span>AI-powered insights</span>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative Options */}
      <section className={styles.alternativeSection}>
        <div className={styles.alternativeGrid}>
          <div className={styles.optionCard}>
            <div className={styles.optionIcon}>
              <i className="fas fa-user-plus"></i>
            </div>
            <div className={styles.optionContent}>
              <h3>Already have an account?</h3>
              <p>Log in to access your saved comparisons and premium features</p>
              <button className={styles.loginButton} onClick={handleLoginRedirect}>
                <i className="fas fa-sign-in-alt"></i>
                Log In to Account
              </button>
            </div>
          </div>

          <div className={styles.optionCard}>
            <div className={styles.optionIcon}>
              <i className="fas fa-phone"></i>
            </div>
            <div className={styles.optionContent}>
              <h3>Need Personal Help?</h3>
              <p>Speak with our construction experts for personalized guidance</p>
              <button className={styles.consultButton}>
                <i className="fas fa-calendar"></i>
                Book Free Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.upgradeModal}>
            <div className={styles.modalHeader}>
              <h2>Unlock Your Complete Analysis</h2>
              <button 
                className={styles.closeModal} 
                onClick={() => setShowUpgradeModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalContent}>
              {selectedPlan && (
                <div className={styles.selectedPlan}>
                  <div className={styles.selectedPlanHeader}>
                    <div className={styles.selectedPlanIcon} style={{ background: selectedPlan.bgGradient }}>
                      <i className={`fas ${selectedPlan.icon}`}></i>
                    </div>
                    <div>
                      <h3>{selectedPlan.name}</h3>
                      <div className={styles.selectedPlanPrice}>
                        <span className={styles.originalPrice}>${selectedPlan.originalPrice}</span>
                        <span className={styles.currentPrice}>${selectedPlan.price}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.modalFeatures}>
                    {selectedPlan.features.map((feature, index) => (
                      <div key={index} className={styles.modalFeature}>
                        <i className="fas fa-check"></i>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className={styles.paymentSection}>
                <div className={styles.paymentForm}>
                  <div className={styles.formGroup}>
                    <label>Email Address</label>
                    <input type="email" placeholder="your@email.com" />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label>Card Information</label>
                    <input type="text" placeholder="1234 5678 9012 3456" />
                    <div className={styles.cardRow}>
                      <input type="text" placeholder="MM / YY" />
                      <input type="text" placeholder="CVC" />
                    </div>
                  </div>
                  
                  <div className={styles.securityNote}>
                    <i className="fas fa-lock"></i>
                    <span>Your payment information is secure and encrypted</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Mock Ad for Testing - Remove when using real ads */}
            <MockAd placement="footer"/>
            <div className={styles.modalFooter}>
              <button 
                className={styles.purchaseButton}
                style={{ background: selectedPlan?.bgGradient }}
              >
                <i className="fas fa-credit-card"></i>
                Complete Purchase - ${selectedPlan?.price}
              </button>
              
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestProposalComparisonView; 